import i18n from '../i18n';

export function isLicenseInvalidError(error) {
  return error?.response?.data?.licenseInvalid === true;
}

export function isUpgradeRequiredError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const details = typeof data.details === 'string' ? data.details : '';
  const message = typeof data.message === 'string' ? data.message : '';
  const combined = `${message} ${details}`.toLowerCase();

  if (data.licenseInvalid === true) return false;

  return (
    data.upgradeRequired === true ||
    status === 402 ||
    combined.includes("limit reached") ||
    combined.includes("upgrade your plan")
  );
}

function uiLanguageBase() {
  return String(i18n.language || 'en').split('-')[0].toLowerCase();
}

/**
 * Detect English API / framework copy that should not win over a localized UI fallback
 * when the Header language is de/fr/nl. Diacritics usually mean already localized.
 */
export function looksLikeUnlocalizedEnglish(text) {
  const msg = String(text || '').trim();
  if (!msg) return false;
  if (/[À-ÖØ-öø-ÿĀ-žĂăȘșȚțẞß]/.test(msg)) return false;
  return (
    /^(Invalid |Failed to |Unable to |Not (found|allowed|authorized)|Access denied|Unauthorized|Forbidden|Bad request|Internal |You (do not|cannot|can't|must|need)|Please |Cannot |Could not |Error[: ]|No [a-z]|Missing |Required |Something went|An error|Request |The (request|user|student|session|enrollment|payment|wallet)|Unexpected |Unknown |Denied|Rejected|Expired|Timeout|Too many|Already |Insufficient |Duplicate |Conflict)/i.test(
      msg,
    )
    || /\b(please try again|try again later|contact (support|your admin)|went wrong)\b/i.test(msg)
  );
}

/** Messages that must never reach end users as-is (setup / SDK / OAuth jargon). */
export function looksLikeTechnicalLeak(text) {
  const raw = String(text || '');
  if (!raw.trim()) return false;
  if (/\[object Object\]/i.test(raw)) return true;
  if (looksLikeRawJson(raw)) return true;
  return /user:read:token|meeting:write:meeting|meeting:read:meeting|Server-to-Server|Meeting SDK|Zoom Marketplace|\bZAK\b|host token \(zak\)|JOIN_MEETING_FAILED|Not support start meeting via tokens|errorCode:\s*\d+|scope missing|OAuth app/i.test(raw);
}

function looksLikeRawJson(text) {
  const trimmed = String(text || '').trim();
  return (trimmed.startsWith('{') && trimmed.endsWith('}'))
    || (trimmed.startsWith('[') && trimmed.endsWith(']'));
}

/** Safe string for alerts / banners — never technical Zoom/OAuth dumps. */
export function safeUserMessage(value, fallbackMessage) {
  const resolvedFallback = fallbackMessage || i18n.t('errors.api.fallback');
  if (value == null) return resolvedFallback;
  if (typeof value !== 'string') {
    if (typeof value?.message === 'string') return safeUserMessage(value.message, resolvedFallback);
    return resolvedFallback;
  }
  const trimmed = value.trim();
  if (!trimmed || /internal server error/i.test(trimmed)) return resolvedFallback;
  if (looksLikeTechnicalLeak(trimmed)) return resolvedFallback;
  if (uiLanguageBase() !== 'en' && looksLikeUnlocalizedEnglish(trimmed)) {
    return resolvedFallback;
  }
  return trimmed;
}

function resolveUserFacingApiText(apiText, fallbackMessage) {
  return safeUserMessage(apiText, fallbackMessage);
}

export function getApiErrorInfo(
  error,
  fallbackMessage,
) {
  const resolvedFallback = fallbackMessage || i18n.t('errors.api.fallback');
  const data = error?.response?.data || {};
  const details = typeof data.details === 'string' ? data.details.trim() : '';
  const message = typeof data.message === 'string' ? data.message.trim() : '';
  const networkMessage =
    typeof error?.message === 'string' ? error.message.trim() : '';

  if (isUpgradeRequiredError(error)) {
    const featureCode = data.featureCode;
    if (featureCode === "ONLINE_PAYMENTS") {
      return {
        title: i18n.t('errors.api.upgradeRequired'),
        message: resolveUserFacingApiText(
          details,
          i18n.t('errors.api.onlinePaymentsUpgrade'),
        ),
      };
    }
    return {
      title: i18n.t('errors.api.planLimitReached'),
      message: resolveUserFacingApiText(
        details,
        i18n.t('errors.api.planLimitMessage'),
      ),
    };
  }

  if (details) {
    return {
      title: i18n.t('errors.api.requestFailed'),
      message: resolveUserFacingApiText(details, resolvedFallback),
    };
  }

  if (message && message.toLowerCase() !== "internal server error") {
    return {
      title: i18n.t('errors.api.requestFailed'),
      message: resolveUserFacingApiText(message, resolvedFallback),
    };
  }

  if (
    networkMessage &&
    !networkMessage.startsWith("Request failed with status code")
  ) {
    return {
      title: i18n.t('errors.api.requestFailed'),
      message: resolveUserFacingApiText(networkMessage, resolvedFallback),
    };
  }

  return {
    title: i18n.t('errors.api.requestFailed'),
    message: resolvedFallback,
  };
}

/** Localized user-facing API error string (prefers UI fallback over English leaks). */
export function apiErrorMessage(error, fallbackMessage) {
  return getApiErrorInfo(error, fallbackMessage).message;
}
